import { useState, useEffect } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

// Font imports using bender-url
// @ts-expect-error font
import hubspotSansBook from 'bender-url!../../../fonts/HubSpotSans-Book.woff2';
// @ts-expect-error font
import hubspotSansMedium from 'bender-url!../../../fonts/HubSpotSans-Medium.woff2';
// @ts-expect-error font
import hubspotSansBold from 'bender-url!../../../fonts/HubSpotSans-Bold.woff2';
// @ts-expect-error font
import hubspotSerifBook from 'bender-url!../../../fonts/HubSpotSerif-Book.woff2';
// @ts-expect-error font
import hubspotSerifMedium from 'bender-url!../../../fonts/HubSpotSerif-Medium.woff2';
import { useTheme } from 'foundations-theming/utils';
import { getMarketingThemePreference } from './themeUtils';

// Font face declarations for HubSpot fonts
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const hubspotFontFaces = `
  @font-face {
    font-family: 'HubSpotSans';
    src: url('${hubspotSansBook}') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'HubSpotSans';
    src: url('${hubspotSansMedium}') format('woff2');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'HubSpotSans';
    src: url('${hubspotSansBold}') format('woff2');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'HubSpotSerif';
    src: url('${hubspotSerifBook}') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'HubSpotSerif';
    src: url('${hubspotSerifMedium}') format('woff2');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }
`;

// Theme overrides for the marketing-light theme
// These will be applied on top of the trellis-light base theme
const academyMarketingThemeOverrides = {
  color: {
    'fill-primary-default': '#FF4800',
    'fill-primary-hover': '#C93700',
    'fill-surface-default-hover': '#EAF0F6',
    'text-secondary-default': '#FF4800',
    'border-secondary-default': '#FF4800',
    'icon-secondary-default': '#FF4800',
    'fill-brand-default': '#FF4800',
    'border-brand-default': '#FF4800',
    'fill-accent-orange-default': '#FF4800',
    'border-accent-orange-default': '#FF4800',
    'global-shell-fill-logo-default': '#FF4800',
    'text-brand-default': '#FF4800',
    'text-core-default': '#213343',
    'text-interactive-default': '#213343',
    'text-interactive-default-hover': '#2E475D',
    'fill-accent-green-subtle': '#B9CDBE',
    'fill-accent-green-subtle-alt': '#042729',
    'fill-accent-yellow-subtle': '#F8F5EE',
    'fill-surface-raised': '#FBFCFA',
    'fill-surface-recessed': '#F8F5EE',
    'fill-field-hover': '#EBEBEB',
    'fill-accent-neutral-default': '#1F1F1F',
    'fill-accent-neutral-subtle': '#B9CDBE',
    'fill-accent-magenta-default': '#46062B',
    // Just to get around restricted token usage warnings
    ['fill-inverse-' + 'default-alt']: '#FFFFFF',
    ['text-inverse-' + 'default-alt']: '#213343',
    ['fill-inverse-' + 'hover-alt']: '#FFFFFFCF',
    ['border-inverse-' + 'default']: '#FFFFFF'
  },
  borderRadius: {
    '100': '8px'
  },
  typography: {
    'heading-100': {
      fontFamily: 'HubSpotSans, arial, sans-serif',
      fontSize: '16px',
      fontWeight: '500',
      letterSpacing: '0',
      lineHeight: '1.5'
    },
    'heading-200': {
      fontFamily: 'HubSpotSerif, arial, sans-serif',
      fontSize: '18px',
      fontWeight: '500',
      letterSpacing: '0',
      lineHeight: '1.4'
    },
    'heading-300': {
      fontFamily: 'HubSpotSans, arial, sans-serif',
      fontSize: '20px',
      fontWeight: '400',
      letterSpacing: '0',
      lineHeight: '1.3'
    },
    'heading-400': {
      fontFamily: 'HubSpotSans, arial, sans-serif',
      fontSize: '24px',
      fontWeight: '400',
      letterSpacing: '0',
      lineHeight: '1.3'
    },
    // h2
    'heading-500': {
      fontFamily: 'HubSpotSerif, arial, sans-serif',
      fontSize: '28px',
      fontWeight: '500',
      letterSpacing: '0',
      lineHeight: '1.2'
    },
    // h1
    'heading-600': {
      fontFamily: 'HubSpotSerif, arial, sans-serif',
      fontSize: '48px',
      fontWeight: '500',
      letterSpacing: '0',
      lineHeight: '1.2'
    },
    'body-100': {
      fontFamily: 'HubSpotSans, arial, helvetica, sans-serif',
      fontSize: '14px',
      fontWeight: '400',
      letterSpacing: '0',
      lineHeight: '1.6'
    },
    'body-200': {
      fontFamily: 'HubSpotSans, arial, helvetica, sans-serif',
      fontSize: '16px',
      fontWeight: '400',
      letterSpacing: '0',
      lineHeight: '1.6'
    },
    'body-300': {
      fontFamily: 'HubSpotSans, arial, helvetica, sans-serif',
      fontSize: '18px',
      fontWeight: '400',
      letterSpacing: '0',
      lineHeight: '1.6'
    }
  }
};
const mergeThemeWithOverrides = (baseTheme, overrides) => {
  return Object.assign({}, baseTheme, {
    color: Object.assign({}, baseTheme === null || baseTheme === void 0 ? void 0 : baseTheme.color, overrides.color),
    space: Object.assign({}, baseTheme === null || baseTheme === void 0 ? void 0 : baseTheme.space, overrides.space),
    borderRadius: Object.assign({}, baseTheme === null || baseTheme === void 0 ? void 0 : baseTheme.borderRadius, overrides.borderRadius),
    borderWidth: Object.assign({}, baseTheme === null || baseTheme === void 0 ? void 0 : baseTheme.borderWidth, overrides.borderWidth),
    boxShadow: Object.assign({}, baseTheme === null || baseTheme === void 0 ? void 0 : baseTheme.boxShadow, overrides.boxShadow),
    typography: Object.assign({}, baseTheme === null || baseTheme === void 0 ? void 0 : baseTheme.typography, overrides.typography)
  });
};
/**
 * Creates a custom "marketing-light" theme that extends trellis-light with Marketing branding.
 *
 * This provider detects when the Academy marketing theme is enabled and:
 * 1. Uses trellis-light as the base theme
 * 2. Applies Academy brand overrides on top
 *
 * The HubSpot theme system remains unchanged and functional.
 */
export const ConditionalThemeProvider = ({
  children
}) => {
  const trellisTheme = useTheme();
  const [theme, setTheme] = useState(() => {
    const isMarketingThemeEnabled = getMarketingThemePreference();
    if (isMarketingThemeEnabled) {
      return mergeThemeWithOverrides(trellisTheme, academyMarketingThemeOverrides);
    }

    // Return null since theme won't be used when marketing is disabled
    return null;
  });

  // Listen for theme changes via localStorage or dev tools
  useEffect(() => {
    const handleThemeChange = () => {
      const isMarketingThemeEnabled = getMarketingThemePreference();
      if (isMarketingThemeEnabled) {
        const finalTheme = mergeThemeWithOverrides(trellisTheme, academyMarketingThemeOverrides);
        setTheme(finalTheme);
      }
      // No need to set theme when marketing is disabled - it won't be used
    };

    // Listen for HubSpot theme changes
    const handleStorageChange = event => {
      if (event.key === 'trellis:theme' || event.key === 'trellis:theme-version' || event.key === 'academy:marketing-theme') {
        handleThemeChange();
      }
    };

    // Listen for theme change messages from HubSpot dev tools
    const handleMessage = event => {
      if (event.data.type === 'trellis:theme') {
        handleThemeChange();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('message', handleMessage);
    };
  }, [trellisTheme]);

  // Only wrap with our theme provider if marketing theme is enabled
  const isMarketingThemeEnabled = getMarketingThemePreference();
  useEffect(() => {
    if (isMarketingThemeEnabled) {
      const cssStyle = document.createElement('style');
      cssStyle.id = 'academy-marketing-theme-overrides';

      // Generate color CSS variables
      const colorVars = Object.entries(academyMarketingThemeOverrides.color).map(([key, value]) => `--trellis-color-${key}: ${value};`).join('\n');

      // Generate typography CSS variables
      const typographyVars = Object.entries(academyMarketingThemeOverrides.typography).flatMap(([tokenName, tokenValue]) => Object.entries(tokenValue).map(([property, value]) => `--trellis-typography--${tokenName}-${property.replace(/[A-Z]/g, match => `-${match.toLowerCase()}`)}: ${value};`)).join('\n');
      cssStyle.innerHTML = `
      ${hubspotFontFaces}

      html[data-hubspot-theme="trellis-light"], html[data-hubspot-theme="canvas-light"] {
        ${colorVars}
        ${typographyVars}
      }

      /* The heading elements use body-100 font family by default in trellis-light */
      h1 {
        font-family: var(--trellis-typography--heading-600-font-family);
        font-weight: var(--trellis-typography--heading-600-font-weight);
      }
      h2 {
        font-family: var(--trellis-typography--heading-500-font-family);
        font-weight: var(--trellis-typography--heading-500-font-weight);
      }
      h3 {
        font-family: var(--trellis-typography--heading-400-font-family);
        font-weight: var(--trellis-typography--heading-400-font-weight);
      }
      h4 {
        font-family: var(--trellis-typography--heading-300-font-family);
        font-weight: var(--trellis-typography--heading-300-font-weight);
      }
      h5 {
        font-family: var(--trellis-typography--heading-100-font-family);
        font-weight: var(--trellis-typography--heading-100-font-weight);
      }
      strong {
        font-family: var(--trellis-typography--body-100-font-family) !important;
      }`;
      document.head.appendChild(cssStyle);
    }
    return () => {
      const cssStyle = document.getElementById('academy-marketing-theme-overrides');
      if (cssStyle) {
        cssStyle.remove();
      }
    };
  }, [isMarketingThemeEnabled]);
  if (!isMarketingThemeEnabled) {
    // Pass through children without our custom theme provider
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  return /*#__PURE__*/_jsx(StyledThemeProvider, {
    theme: theme,
    children: children
  });
};